<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import { Collapse, Container, Icon, Stack, Text } from '@Shelly/shelly-design-system';

	import type { Device } from 'src/types/Device';
	import { user_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { getCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import { getDeviceImage } from 'src/helpers/device_state_view_helpers';

	/**
	 * Picker that lists every camera the user owns, grouped by House
	 * and then by Room. Used by the ManageCameraCloudStorage modal in
	 * two modes:
	 *
	 *   - `mode === 'multi'`: assign multiple cameras to free slots
	 *     (capped at `maxSelection`).
	 *   - `mode === 'single'`: pick exactly one replacement camera for
	 *     a slot that is being edited.
	 *
	 * Both the House and Room headers are clickable Collapse rows —
	 * the design-system Collapse doesn't auto-toggle, it just dispatches
	 * a `click` event, so we drive the `expanded` state locally.
	 */
	export let mode: 'multi' | 'single' = 'multi';
	export let maxSelection = 1;
	export let occupiedCameraIds: Set<string> = new Set();
	export let initialSelection: string[] = [];

	const dispatch = createEventDispatcher<{ change: { cameraIds: string[] } }>();

	type RoomGroup = { id: string; roomName: string; cameras: Device[] };
	type HouseGroup = { id: string; houseName: string; rooms: RoomGroup[] };

	let selection: string[] = [...initialSelection];
	let houseExpanded: Record<string, boolean> = {};
	let roomExpanded: Record<string, boolean> = {};

	$: cameraDevices = getCameraDevices($devices ?? {});
	$: groupedByHouse = groupCamerasByHouseAndRoom(cameraDevices);

	/** Expand the first house and all its rooms on first paint so cameras are visible immediately. */
	$: if (groupedByHouse.length && Object.keys(houseExpanded).length === 0) {
		const first = groupedByHouse[0];
		houseExpanded = { [first.id]: true };
		roomExpanded = first.rooms.reduce<Record<string, boolean>>((acc, r) => {
			acc[r.id] = true;
			return acc;
		}, {});
	}

	function getRoomName(roomId: number | undefined): string {
		if (roomId == null) return '';
		const room = $rooms?.[roomId];
		return room?.name ?? '';
	}

	/**
	 * Groups cameras under the active unit's name by room label. The
	 * unit name comes from `user_data.available_units[user_id]` — this
	 * is the source of truth used by the unit switcher dropdown and by
	 * `AccountSharingSettingsV2`. We *cannot* read `home_data.name`
	 * here (FE-1742): `home_data` is a per-user preferences blob
	 * (`globalLocalControl`, `promotions`, vouchers baseline, …) with
	 * no `name` field, so the lookup always silently fell back to the
	 * i18n default "My Home" — regardless of which unit was active.
	 * @todo Multi-home — replace with real `sub_units` per-house grouping when available.
	 */
	function groupCamerasByHouseAndRoom(allCameras: Device[]): HouseGroup[] {
		const currentUnitId = String($user_data?.user_id ?? '');
		const houseName: string =
			$user_data?.available_units?.[currentUnitId] ||
			$format('text_my_home', { default: 'My Home' });

		const otherRoomLabel = $format('text_no_room_label', { default: 'Other' });

		const byRoom = new Map<string, { roomName: string; cameras: Device[] }>();
		for (const camera of allCameras) {
			const resolved = getRoomName(camera.room_id);
			const roomName = resolved || otherRoomLabel;
			const existing = byRoom.get(roomName);
			if (existing) {
				existing.cameras.push(camera);
			} else {
				byRoom.set(roomName, { roomName, cameras: [camera] });
			}
		}

		const houseId = `house-${houseName}`;
		const rooms: RoomGroup[] = Array.from(byRoom.values()).map((group) => ({
			id: `${houseId}-room-${group.roomName}`,
			roomName: group.roomName,
			cameras: group.cameras,
		}));

		return [{ id: houseId, houseName, rooms }];
	}

	function toggleHouse(houseId: string) {
		houseExpanded = { ...houseExpanded, [houseId]: !houseExpanded[houseId] };
	}

	function toggleRoom(roomId: string) {
		roomExpanded = { ...roomExpanded, [roomId]: !roomExpanded[roomId] };
	}

	function isSelected(cameraId: string): boolean {
		return selection.includes(cameraId);
	}

	/**
	 * In `single` mode, re-clicking the selected camera is a no-op (radio semantics).
	 */
	function toggleSelection(cameraId: string) {
		if (occupiedCameraIds.has(cameraId) && !initialSelection.includes(cameraId)) {
			return;
		}
		if (mode === 'single') {
			if (isSelected(cameraId)) return;
			selection = [cameraId];
		} else if (isSelected(cameraId)) {
			selection = selection.filter((id) => id !== cameraId);
		} else if (selection.length < maxSelection) {
			selection = [...selection, cameraId];
		}
		dispatch('change', { cameraIds: selection });
	}
</script>

<Container padding="0" type="modalContainer">
	{#if cameraDevices.length === 0}
		<div class="text-center mt-m mb-m ml-m mr-m">
			<Text class="text-color-muted">
				{$format('text_no_cameras_available', {
					default: "You don't have any cameras yet.",
				})}
			</Text>
		</div>
	{:else}
		<Stack gap="s" class="mt-m mb-m ml-m mr-m">
			{#each groupedByHouse as house (house.id)}
				<Collapse
					id={house.id}
					title={$format('text_house_label', {
						default: `House: ${house.houseName}`,
						values: { name: house.houseName },
					})}
					expanded={!!houseExpanded[house.id]}
					on:click={() => toggleHouse(house.id)}
				>
					<Stack gap="xs">
						{#each house.rooms as room (room.id)}
							<Collapse
								id={room.id}
								title={room.roomName}
								expanded={!!roomExpanded[room.id]}
								on:click={() => toggleRoom(room.id)}
							>
								<Stack gap="xs">
									{#each room.cameras as camera (camera.id)}
										{@const occupiedElsewhere =
											occupiedCameraIds.has(camera.id) &&
											!initialSelection.includes(camera.id)}
										{@const checked = selection.includes(camera.id)}
										{@const reachedCap =
											mode === 'multi' &&
											!checked &&
											selection.length >= maxSelection}
										{@const disabled = occupiedElsewhere || reachedCap}
										{@const visuallyChecked = checked || occupiedElsewhere}
										{@const cameraImage = getDeviceImage(camera)}
										<button
											type="button"
											class="ccs-camera-row"
											class:ccs-camera-row--checked={checked}
											class:ccs-camera-row--disabled={disabled}
											{disabled}
											on:click={() => toggleSelection(camera.id)}
											aria-label={$format('label_select_camera', {
												default: `Select camera ${camera.name}`,
												values: { name: camera.name },
											})}
										>
											<div
												class="d-flex align-items-center gap-s flex-1 min-width-0"
											>
												<div class="ccs-device-avatar">
													{#if cameraImage}
														<img src={cameraImage} alt={camera.name} />
													{:else}
														<Icon icon="fas fa-camera" />
													{/if}
												</div>
												<div class="flex-1 min-width-0">
													<Text class="text-bold" styleType="body-small">
														{camera.name}
													</Text>
													{#if getRoomName(camera.room_id)}
														<Text
															styleType="body-extra-small"
															class="text-color-muted"
														>
															{getRoomName(camera.room_id)}
														</Text>
													{/if}
												</div>
											</div>
											{#if mode === 'single'}
												<span
													class="ccs-radio"
													class:ccs-radio--checked={visuallyChecked}
													class:ccs-radio--locked={occupiedElsewhere}
													aria-hidden="true"
												></span>
											{:else}
												<div
													class="ccs-checkbox"
													class:ccs-checkbox--checked={visuallyChecked}
													class:ccs-checkbox--locked={occupiedElsewhere}
												>
													{#if visuallyChecked}
														<Icon icon="fas fa-check" size="s" />
													{/if}
												</div>
											{/if}
										</button>
									{/each}
								</Stack>
							</Collapse>
						{/each}
					</Stack>
				</Collapse>
			{/each}
		</Stack>
	{/if}
</Container>

<style>
	.flex-1 {
		flex: 1;
	}

	.min-width-0 {
		min-width: 0;
	}

	.ccs-camera-row {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		background: var(--color-secondary-1, #242527);
		border: 1px solid var(--color-secondary-2, #303235);
		border-radius: 12px;
		padding: 12px;
		color: inherit;
		text-align: left;
		cursor: pointer;
		transition:
			border-color 0.2s ease-in-out,
			opacity 0.2s ease-in-out;
	}

	.ccs-camera-row:hover:not(.ccs-camera-row--disabled) {
		border-color: #893bf2;
	}

	.ccs-camera-row--checked {
		border-color: #893bf2;
		background: rgba(137, 59, 242, 0.08);
	}

	.ccs-camera-row--disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.ccs-checkbox {
		width: 22px;
		height: 22px;
		border-radius: 6px;
		border: 1.5px solid var(--color-secondary-3, #5b5f66);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.ccs-checkbox--checked {
		background: linear-gradient(135deg, #d56cf8 0%, #893bf2 100%);
		border-color: transparent;
		color: #ffffff;
	}

	/*
	 * "Locked" appearance for the checkbox — used when the camera is
	 * already assigned to another slot, so the visual conveys
	 * "already in use" rather than "currently picked".
	 */
	.ccs-checkbox--locked {
		background: var(--color-secondary-3, #5b5f66);
		color: #ffffff;
	}

	/*
	 * Radio button used by the single-select (Replace camera) mode.
	 * The inner dot is rendered with a pseudo-element so the
	 * gradient/ring stays consistent with the rest of the picker.
	 */
	.ccs-radio {
		width: 22px;
		height: 22px;
		border-radius: 50%;
		border: 1.5px solid var(--color-secondary-3, #5b5f66);
		flex-shrink: 0;
		position: relative;
		display: inline-block;
		background: transparent;
	}

	.ccs-radio--checked {
		border-color: #893bf2;
	}

	.ccs-radio--checked::after {
		content: '';
		position: absolute;
		inset: 3px;
		border-radius: 50%;
		background: linear-gradient(135deg, #d56cf8 0%, #893bf2 100%);
	}

	/*
	 * "Locked" radio (camera is in another slot) — neutralise the
	 * purple branding and use a flat grey ring + dot so the user
	 * reads it as "taken" rather than "your current pick".
	 */
	.ccs-radio--locked {
		border-color: var(--color-secondary-3, #5b5f66);
	}

	.ccs-radio--locked::after {
		background: var(--color-secondary-3, #5b5f66);
	}

	/*
	 * Square thumbnail for the camera row — matches the slot-row avatar
	 * in `ManageCameraCloudStorageModal` so the picker and slot list
	 * read as the same visual language.
	 */
	.ccs-device-avatar {
		width: 36px;
		height: 36px;
		border-radius: 8px;
		background: var(--color-secondary-2, #303235);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		flex-shrink: 0;
	}

	.ccs-device-avatar img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
</style>
